// Permessi per ruolo: una sola tabella per client e server. Erano due copie
// identiche (apps/web e apps/api), libere di divergere: la SPA avrebbe
// mostrato un'azione che l'API poi rifiuta, o il contrario.

export type UserRole = 'superadmin' | 'admin' | 'referent' | 'member';

export type Resource =
	| 'org'
	| 'user'
	| 'member'
	| 'pod'
	| 'incentive'
	| 'change_request'
	| 'notification'
	| 'document'
	| 'energy_reading'
	| 'report'
	| 'invite';

export type Action = 'read' | 'create' | 'update' | 'delete' | 'manage';
export type Permission = `${Resource}.${Action}`;

export const ROLE_LEVEL: Record<UserRole, number> = {
	superadmin: 40,
	admin: 30,
	referent: 20,
	member: 10,
};

const RESOURCES: Resource[] = [
	'org',
	'user',
	'member',
	'pod',
	'incentive',
	'change_request',
	'notification',
	'document',
	'energy_reading',
	'report',
	'invite',
];
const ACTIONS: Action[] = ['read', 'create', 'update', 'delete', 'manage'];

const ALL_PERMS: Permission[] = RESOURCES.flatMap((r) =>
	ACTIONS.map((a) => `${r}.${a}` as Permission),
);

const ADMIN_PERMS: Permission[] = [
	'org.read',
	'user.read',
	'user.create',
	'user.update',
	'member.read',
	'member.create',
	'member.update',
	'member.delete',
	'member.manage',
	'pod.read',
	'pod.create',
	'pod.update',
	'pod.delete',
	'pod.manage',
	'incentive.read',
	'incentive.create',
	'incentive.update',
	'incentive.delete',
	'incentive.manage',
	'change_request.read',
	'change_request.create',
	'change_request.update',
	'change_request.manage',
	'notification.read',
	'notification.create',
	'notification.update',
	'notification.manage',
	'document.read',
	'document.create',
	'document.update',
	'document.delete',
	'document.manage',
	'energy_reading.read',
	'energy_reading.create',
	'energy_reading.update',
	'energy_reading.manage',
	'report.read',
	'report.create',
	'report.update',
	'report.manage',
	'invite.create',
];

// Referente di una configurazione: gestisce operativamente la propria CER
// (membri, pod, incentivi, documenti, richieste, letture, report, inviti) ma
// NON la gestione degli utenti/ruoli (user.*), riservata all'amministratore.
const REFERENT_PERMS: Permission[] = ADMIN_PERMS.filter((p) => !p.startsWith('user.'));

const MEMBER_PERMS: Permission[] = [
	'org.read',
	'member.read',
	'pod.read',
	'incentive.read',
	'change_request.read',
	'change_request.create',
	'notification.read',
	'document.read',
	'energy_reading.read',
];

export const ROLE_PERMISSIONS: Record<UserRole, ReadonlySet<Permission>> = {
	superadmin: new Set(ALL_PERMS),
	admin: new Set(ADMIN_PERMS),
	referent: new Set(REFERENT_PERMS),
	member: new Set(MEMBER_PERMS),
};

export function can(role: UserRole, perm: Permission): boolean {
	return ROLE_PERMISSIONS[role].has(perm);
}

export function hasRoleAtLeast(role: UserRole, minimum: UserRole): boolean {
	return ROLE_LEVEL[role] >= ROLE_LEVEL[minimum];
}

/** An actor can assign/promote a target role only if it is strictly below their own level. */
export function canAssignRole(actor: UserRole, target: UserRole): boolean {
	return ROLE_LEVEL[actor] > ROLE_LEVEL[target];
}
